import { useQuery } from '@tanstack/react-query';
import { listPartyGroups } from '@/app/services/partyService';

// Party group whose customers are intentionally left un-delivered for long
// periods (invoices stay editable across multiple deliveries). They pollute the
// header delivery-status badges, so we exclude this group from those counts/lists.
export const CORPORATE_SALES_GROUP_NAME = 'CORPORATE SALES';

interface CorporateSalesGroup {
  /** Group id, or `undefined` if the org has no CORPORATE SALES group. */
  groupId: number | undefined;
  /**
   * False until the lookup settles. Callers whose request shape depends on
   * `groupId` must wait for this — firing early sends one request without the
   * exclusion and a second, identical-looking one with it.
   */
  isResolved: boolean;
}

/**
 * Resolves the CORPORATE SALES party group id for the current organization by
 * name (org-safe; ids differ per org). Returns `undefined` if the group does
 * not exist — callers then simply skip the exclusion (today's behavior).
 */
export const useCorporateSalesGroupId = (): CorporateSalesGroup => {
  const { data, status } = useQuery({
    queryKey: ['corporate-sales-group-id'],
    queryFn: () =>
      listPartyGroups({
        filters: JSON.stringify([
          { field: 'name', op: 'eq', value: CORPORATE_SALES_GROUP_NAME },
        ]),
        limit: 1,
      }),
    staleTime: 15 * 60 * 1000,
    // One retry only: dependents block on this settling, so don't make them
    // wait through the default three-attempt backoff.
    retry: 1,
  });

  return {
    groupId: data?.items?.[0]?.id,
    // 'error' counts as resolved — dependents proceed without the exclusion.
    isResolved: status !== 'pending',
  };
};
